import { Component, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { ClipboardModule, Clipboard } from '@angular/cdk/clipboard';
import {
  LucideAngularModule,
  CheckCircle,
  Leaf,
  TreePine,
  Droplets,
  Download,
  Clock,
  Copy,
  Building2,
  FileText,
  ArrowRight,
} from 'lucide-angular';

import { ContributeWizardService } from '../services/contribute-wizard.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-contribution-success',
  standalone: true,
  imports: [CommonModule, TranslocoModule, ButtonModule, LucideAngularModule, ClipboardModule],
  templateUrl: './contribution-success.component.html',
})
export class ContributionSuccessComponent {
  private router = inject(Router);
  private transloco = inject(TranslocoService);
  private clipboard = inject(Clipboard);
  protected wizardService = inject(ContributeWizardService);

  checkCircleIcon = CheckCircle;
  leafIcon = Leaf;
  treePineIcon = TreePine;
  dropletsIcon = Droplets;
  downloadIcon = Download;
  clockIcon = Clock;
  copyIcon = Copy;
  buildingIcon = Building2;
  fileTextIcon = FileText;
  arrowRightIcon = ArrowRight;

  // Computed properties for state management
  isPaid = computed(
    () => this.wizardService.contributionResult()?.status === 'PAID'
  );

  isBankTransferPending = computed(
    () =>
      this.wizardService.selectedPaymentMethod() === 'BANK_TRANSFER' &&
      !this.isPaid()
  );

  bankDetails = computed(() => this.wizardService.bankDetails());

  // CO2 in kg - use stored value from contribution result (after Stripe redirect)
  // or computed value from wizard (during normal flow)
  co2CompensatedKg = computed(() => {
    const result = this.wizardService.contributionResult();
    if (result?.co2Compensated) {
      return result.co2Compensated;
    }
    return this.wizardService.co2ToCompensate();
  });

  // CO2 in tonnes
  co2CompensatedTonnes = computed(() => this.co2CompensatedKg() / 1000);

  // Total amount - use stored value from contribution result
  storedTotalAmount = computed(() => {
    const result = this.wizardService.contributionResult();
    if (result?.totalAmount) {
      return result.totalAmount;
    }
    return this.wizardService.totalAmount();
  });

  getProjectName(): string {
    const project = this.wizardService.selectedProject();
    if (!project) return '';

    const lang = this.transloco.getActiveLang().toUpperCase();
    if (lang === 'FR') return project.nameFR || project.nameEN;
    if (lang === 'NL') return project.nameNL || project.nameEN;
    return project.nameEN;
  }

  getProjectDescription(): string {
    const project = this.wizardService.selectedProject();
    if (!project) return '';

    const lang = this.transloco.getActiveLang().toUpperCase();
    if (lang === 'FR') return project.shortDescriptionFR || project.shortDescriptionEN;
    if (lang === 'NL') return project.shortDescriptionNL || project.shortDescriptionEN;
    return project.shortDescriptionEN;
  }

  getTreesEquivalent(): number {
    // Approximation: 1 tree absorbs ~22kg CO2/year
    return Math.round(this.co2CompensatedKg() / 22);
  }

  getKmEquivalent(): number {
    // Approximation: Average car emits ~120g CO2/km
    return Math.round((this.co2CompensatedKg() * 1000) / 120);
  }

  getWaterEquivalent(): number {
    // Approximation: 1kg CO2 = ~500L water (carbon footprint of bottled water)
    return Math.round(this.co2CompensatedKg() * 500);
  }

  // Certificate recipient name - use stored value from contribution result (after Stripe redirect)
  // or from wizard state (during normal flow)
  getCertificateRecipientName(): string {
    const result = this.wizardService.contributionResult();
    if (result?.certificateRecipientName) {
      return result.certificateRecipientName;
    }
    return this.wizardService.certificateRecipientName();
  }

  navigateToContributions(): void {
    this.router.navigate(['/home/contributions']);
  }

  navigateToTravelBoard(): void {
    this.router.navigate(['/home/travel-board']);
  }

  downloadCertificate(): void {
    const url = this.wizardService.getCertificateUrl();
    if (url) {
      window.open(url, '_blank');
    }
  }

  hasCertificate(): boolean {
    return !!this.wizardService.getCertificateUrl();
  }

  copyToClipboard(text: string): void {
    this.clipboard.copy(text);
  }

  /**
   * Navigate to the contribution detail page
   */
  navigateToContribution(): void {
    const contributionId = this.wizardService.contributionResult()?.id;
    if (contributionId) {
      this.router.navigate(['/home/contributions', contributionId]);
    }
  }

  /**
   * Download proforma invoice for bank transfers
   */
  downloadProformaInvoice(): void {
    const contributionId = this.wizardService.contributionResult()?.id;
    if (contributionId) {
      // Use the same endpoint as paid invoices - backend handles proforma for bank transfers
      const url = `${environment.apiUrl}/contributions/${contributionId}/invoice`;
      window.open(url, '_blank');
    }
  }

  /**
   * Check if proforma invoice is available (generated async by backend)
   * For now, always return true as the invoice should be generated quickly
   */
  hasProformaInvoice(): boolean {
    // The proforma invoice is generated asynchronously by the backend
    // We assume it's available after a short delay
    return !!this.wizardService.contributionResult()?.id;
  }
}
